import { useState } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import {
  attachableBlocks,
  materialKind,
  materialSkillIds,
  materialStatus,
  skillDomainKeys,
  skillLabels,
  type PlanBlock,
} from '../../store/selectors'
import { difficultyLabel, isPrintable, kindLabel, readSpec } from '../../lib/specs'
import { formatRelative, todayISO } from '../../lib/dates'
import { domainColor } from '../../lib/rubric'
import { KindMark } from './KindMark'

/**
 * One made thing.
 *
 * The card answers what it is, how hard it is, and what it is for, before it
 * offers anything to press — a shelf of unlabelled thumbnails is unusable once
 * there are thirty of them. The colour is the domain of the first skill it
 * targets, the same hue the block wears on the Today screen, so a material and
 * the block it belongs to look related.
 */
export function MaterialCard({
  artifact,
  reason,
  onPrint,
}: {
  artifact: any
  /** Why it is being suggested, on the rail. */
  reason?: string
  onPrint?: (artifactId: string) => void
}) {
  const [menu, setMenu] = useState<'none' | 'attach' | 'delete'>('none')
  const labels = useAuroraStore(skillLabels)
  const domains = useAuroraStore(skillDomainKeys)
  const date = useAuroraStore((s: any) => s.ui.selectedDate ?? todayISO())
  const blocks = useAuroraStore((s: any) => attachableBlocks(s, date))

  const status = materialStatus(artifact)
  const read = readSpec(artifact)
  const spec = read.ok ? read.spec : undefined
  const kind = materialKind(artifact)
  const skills = materialSkillIds(artifact)
  const domain = domains[skills[0] ?? '']
  const printable = isPrintable(kind)
  const attached = (artifact.playCount ?? 0) > 0

  const act = () => auroraStore.getState() as any

  return (
    <article
      className={`mat${status === 'ready' ? '' : ' is-' + status}`}
      style={{ '--domain-color': domainColor(domain) } as React.CSSProperties}
    >
      <button
        className="mat-face"
        onClick={() => status === 'ready' && act().enterKidMode(artifact._id)}
        disabled={status !== 'ready'}
        aria-label={status === 'ready' ? `Play ${artifact.title}` : artifact.title}
      >
        <span className="mat-thumb" aria-hidden="true">
          <KindMark kind={kind} />
        </span>
        <span className="mat-body">
          <span className="mat-meta">
            <span className="pill pill-domain">{kindLabel(kind)}</span>
            {spec && <span className="mat-difficulty">{difficultyLabel(spec.difficulty)}</span>}
          </span>
          <span className="mat-title">{artifact.title}</span>
          {reason ? (
            <span className="mat-reason">{reason}</span>
          ) : (
            artifact.summary && <span className="mat-summary">{artifact.summary}</span>
          )}
        </span>
      </button>

      {skills.length > 0 && (
        <ul className="mat-skills">
          {skills.slice(0, 3).map((id) => (
            <li key={id} className="mat-skill">
              {labels[id] ?? id}
            </li>
          ))}
          {skills.length > 3 && <li className="mat-skill is-more">+{skills.length - 3}</li>}
        </ul>
      )}

      {status === 'unplayable' && (
        <p className="mat-note">
          Made before the current activity format. Ask Aurora to rebuild it and it will play again.
        </p>
      )}
      {status === 'failed' && (
        <p className="mat-note">
          {artifact.generation?.error ?? 'This one did not come back. Asking again usually works.'}
        </p>
      )}

      <footer className="mat-actions">
        {status === 'ready' && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => act().enterKidMode(artifact._id)}
          >
            <PlayIcon /> Play
          </button>
        )}
        {status !== 'ready' && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => {
              const s = act()
              s.setComposing(`Rebuild "${artifact.title}" as a playable activity`)
              s.setView('thread')
            }}
          >
            Rebuild
          </button>
        )}

        {status === 'ready' && blocks.length > 0 && (
          <button
            className="btn btn-quiet btn-sm"
            aria-expanded={menu === 'attach'}
            onClick={() => setMenu((m) => (m === 'attach' ? 'none' : 'attach'))}
          >
            <PinIcon /> Attach
          </button>
        )}

        {printable && onPrint && (
          <button className="btn btn-quiet btn-sm" onClick={() => onPrint(artifact._id)}>
            <PrintIcon /> Print
          </button>
        )}

        <button
          className="btn btn-quiet btn-sm mat-delete"
          aria-expanded={menu === 'delete'}
          onClick={() => setMenu((m) => (m === 'delete' ? 'none' : 'delete'))}
        >
          <TrashIcon />
          <span className="sr-only">Delete {artifact.title}</span>
        </button>
      </footer>

      {menu === 'attach' && (
        <div className="mat-menu">
          <p className="mat-menu-head">Put it on a block today</p>
          {blocks.map((b: PlanBlock) => (
            <button
              key={b.id}
              className="mat-menu-item"
              onClick={() => {
                act().attachMaterialToBlock(artifact._id, b.id)
                setMenu('none')
              }}
            >
              {b.title}
            </button>
          ))}
        </div>
      )}

      {menu === 'delete' && (
        <div className="mat-menu">
          <p className="mat-menu-head">Delete this one?</p>
          <div className="row row-2">
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => {
                act().deleteArtifact(artifact._id)
                setMenu('none')
              }}
            >
              Delete
            </button>
            <button className="btn btn-quiet btn-sm" onClick={() => setMenu('none')}>
              Keep it
            </button>
          </div>
        </div>
      )}

      {attached && (
        <p className="mat-played tnum">
          Played {artifact.playCount === 1 ? 'once' : `${artifact.playCount} times`}
          {artifact.lastPlayedAt ? ` · ${formatRelative(artifact.lastPlayedAt)}` : ''}
        </p>
      )}
    </article>
  )
}

const S = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

const PlayIcon = () => (
  <svg {...S} width="13" height="13">
    <path d="M7 4.5l12 7.5-12 7.5z" />
  </svg>
)
const PinIcon = () => (
  <svg {...S} width="13" height="13" strokeWidth={1.9}>
    <path d="M12 21v-7M8 3h8l-1 6 3 3H6l3-3z" />
  </svg>
)
const PrintIcon = () => (
  <svg {...S} width="13" height="13" strokeWidth={1.9}>
    <path d="M7 9V3h10v6M7 18H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2" />
    <rect x="7" y="15" width="10" height="6" rx="1" />
  </svg>
)
const TrashIcon = () => (
  <svg {...S} width="13" height="13" strokeWidth={1.9}>
    <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />
  </svg>
)
